// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
// feature driver-cards-v2
import {
  cloneTimelineLoad,
  areTimelineLoadsEqual,
} from '../../timeline';
import { isRouteBuilderLoad, isTimelineEditAllowed } from '../../edit-locks';
import {
  EDIT_COMMAND_INSERT_LOAD,
  EDIT_COMMAND_HYDRATE_SERVER_EVENTS,
  EDIT_COMMAND_RECONCILE_SERVER_LOAD,
} from '../../constants';
//////////////////////////////////////////////////

export const EDIT_SYNC_RESOLUTION_KEEP_LOCAL = 'KEEP_LOCAL';
export const EDIT_SYNC_RESOLUTION_USE_SERVER = 'USE_SERVER';

const RATE_KEY = 'RATE';
const START_KEY = 'START';
const END_KEY = 'END';
const EVENT_KEY_PREFIX = 'EVENT:';
const EVENT_DETAILS_KEY_PREFIX = 'EVENT_DETAILS:';
const EVENT_STRUCTURE_KEY_PREFIX = 'EVENT_STRUCTURE:';
const EDIT_SYNC_CONFLICT_LOCAL_SCHEDULE_INVALID = 'LOCAL_SCHEDULE_INVALID';
const EDIT_SYNC_CONFLICT_EVENT_STRUCTURE_CHANGED = 'EVENT_STRUCTURE_CHANGED';
const EDIT_SYNC_CONFLICT_SAME_TIMELINE_FIELD_CHANGED = 'SAME_TIMELINE_FIELD_CHANGED';

const sameTime = (left, right) => {
  const leftTime = new Date(left).getTime();
  const rightTime = new Date(right).getTime();

  if (!Number.isFinite(leftTime) || !Number.isFinite(rightTime)) return left === right;

  return leftTime === rightTime;
};

const sameValue = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const withoutKey = (object, key) => Object.entries(object || {}).reduce((result, [name, value]) => (
  name === key ? result : { ...result, [name]: value }
), {});

const mergeDefined = (base, update) => {
  const result = { ...(base || {}) };

  Object.entries(update || {}).forEach(([key, value]) => {
    if (value !== undefined) result[key] = value;
  });

  return result;
};

const getEventById = (load) => new Map(
  ((load && load.events) || []).map((event) => [event.id, event]),
);

const getRecordedDraftEventChanges = (load) => new Set(
  Object.keys(((load && load.draftChanges) || {}).events || {}).map(
    (eventId) => `${EVENT_DETAILS_KEY_PREFIX}${eventId}`,
  ),
);

const getEditableChanges = (before, after) => {
  const changes = new Set();

  if (!before || !after) return changes;

  if (!sameTime(before.startAt, after.startAt)) changes.add(START_KEY);

  if (!sameTime(before.endAt, after.endAt)) changes.add(END_KEY);

  if (!sameValue(before.rate, after.rate)) changes.add(RATE_KEY);

  const beforeEvents = getEventById(before);
  const afterEvents = getEventById(after);
  const eventIds = new Set([...beforeEvents.keys(), ...afterEvents.keys()]);

  eventIds.forEach((id) => {
    const beforeEvent = beforeEvents.get(id);
    const afterEvent = afterEvents.get(id);

    if (!beforeEvent || !afterEvent) {
      changes.add(`${EVENT_STRUCTURE_KEY_PREFIX}${id}`);

      return;
    }

    if (!sameTime(beforeEvent.at, afterEvent.at)) {
      changes.add(`${EVENT_KEY_PREFIX}${id}`);
    }

    if (
      !sameValue(beforeEvent.source, afterEvent.source)
      || !sameValue(beforeEvent.location, afterEvent.location)
    ) changes.add(`${EVENT_DETAILS_KEY_PREFIX}${id}`);
  });

  return changes;
};

const getRebaseChanges = (before, after, rateOnly) => {
  const changes = getEditableChanges(before, after);

  return rateOnly
    ? new Set([...changes].filter((key) => key === RATE_KEY))
    : changes;
};

const hasIntersection = (left, right) => [...left].some((key) => right.has(key));

const getMissingLocallyChangedEvents = (changes, serverLoad) => {
  const serverEvents = getEventById(serverLoad);

  return [...changes].filter((key) => (
    (
      key.startsWith(EVENT_KEY_PREFIX)
      || key.startsWith(EVENT_DETAILS_KEY_PREFIX)
    ) && !serverEvents.has(key.slice(key.indexOf(':') + 1))
  ));
};

const copyLocalSessionFields = (target, source) => {
  const result = { ...target };

  ['pendingDriverRate', 'ratePreparationId'].forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(source || {}, field)) result[field] = source[field];
  });

  return result;
};

const applyEditableChanges = (base, snapshot, changes) => {
  const result = copyLocalSessionFields(cloneTimelineLoad(base), snapshot);
  const snapshotEvents = getEventById(snapshot);

  if (changes.has(START_KEY)) result.startAt = snapshot.startAt;

  if (changes.has(END_KEY)) result.endAt = snapshot.endAt;

  if (changes.has(RATE_KEY)) result.rate = snapshot.rate;

  result.events = result.events.map((event) => {
    const snapshotEvent = snapshotEvents.get(event.id);

    if (!snapshotEvent) return event;

    const timeChanged = changes.has(`${EVENT_KEY_PREFIX}${event.id}`);
    const detailsChanged = changes.has(`${EVENT_DETAILS_KEY_PREFIX}${event.id}`);

    return {
      ...event,
      at: timeChanged ? snapshotEvent.at : event.at,
      source: detailsChanged ? snapshotEvent.source : event.source,
      location: detailsChanged ? snapshotEvent.location : event.location,
    };
  });

  if ([...changes].some((key) => key.startsWith(EVENT_DETAILS_KEY_PREFIX))) {
    result.draftChanges = snapshot.draftChanges;
  }

  return result;
};

const mergeServerLoad = (existingLoad, serverLoad) => {
  const existingEvents = getEventById(existingLoad);
  const merged = mergeDefined(existingLoad, serverLoad);

  merged.source = mergeDefined(existingLoad && existingLoad.source, serverLoad && serverLoad.source);
  merged.events = (serverLoad.events || []).map((serverEvent) => {
    const existingEvent = existingEvents.get(serverEvent.id);
    const event = mergeDefined(existingEvent, serverEvent);

    return {
      ...event,
      source: mergeDefined(existingEvent && existingEvent.source, serverEvent.source),
    };
  });

  return merged;
};

const replaceLoad = (loads, loadId, replacement) => loads.map((load) => (
  load.id === loadId ? cloneTimelineLoad(replacement) : load
));

const rebaseUndoStack = (stack, loadId, serverLoad, draftLoad, baselineExists, rateOnly) => {
  let current = baselineExists ? cloneTimelineLoad(serverLoad) : null;
  let lastTargetIndex = -1;

  const result = stack.map((command, index) => {
    if (command.loadId !== loadId) return command;

    lastTargetIndex = index;

    if (!command.before) {
      const changes = getRebaseChanges(serverLoad, command.after, rateOnly);
      const after = applyEditableChanges(serverLoad, command.after, changes);

      current = after;

      return { ...command, before: null, after };
    }

    if (!command.after) {
      const before = current || mergeServerLoad(serverLoad, command.before);

      current = null;

      return { ...command, before, after: null };
    }

    const before = current || cloneTimelineLoad(serverLoad);
    const changes = getRebaseChanges(command.before, command.after, rateOnly);
    const after = applyEditableChanges(before, command.after, changes);

    current = after;

    return { ...command, before, after };
  });

  if (
    lastTargetIndex >= 0
    && current
    && !areTimelineLoadsEqual([current], [draftLoad])
  ) {
    result[lastTargetIndex] = { ...result[lastTargetIndex], after: cloneTimelineLoad(draftLoad) };
  }

  return result;
};

const rebaseRedoStack = (stack, loadId, serverLoad, draftLoad, rateOnly) => {
  const result = [...stack];
  let current = cloneTimelineLoad(draftLoad);

  for (let index = result.length - 1; index >= 0; index -= 1) {
    const command = result[index];

    if (command.loadId === loadId) {
      if (!command.before) {
        const changes = getRebaseChanges(serverLoad, command.after, rateOnly);
        const after = applyEditableChanges(serverLoad, command.after, changes);

        result[index] = { ...command, before: null, after };
        current = after;
      } else if (!command.after) {
        result[index] = { ...command, before: current, after: null };
        current = null;
      } else {
        const before = current || cloneTimelineLoad(serverLoad);
        const changes = getRebaseChanges(command.before, command.after, rateOnly);
        const after = applyEditableChanges(before, command.after, changes);

        result[index] = { ...command, before, after };
        current = after;
      }
    }
  }

  return result;
};

const getConflict = ({
  candidate,
  serverLoad,
  localChanges,
  serverChanges,
  timelineRange,
}) => {
  const timelineChanges = new Set(
    [...localChanges].filter((key) => (
      key !== RATE_KEY && !key.startsWith(EVENT_DETAILS_KEY_PREFIX)
    )),
  );

  const missingEvents = getMissingLocallyChangedEvents(localChanges, serverLoad);

  const candidateAllowed = !timelineChanges.size || isTimelineEditAllowed(serverLoad, {
    after: candidate,
    before: serverLoad,
    loadId: serverLoad.id,
    kind: EDIT_COMMAND_RECONCILE_SERVER_LOAD,
  }, timelineRange);

  if (missingEvents.length) {
    return {
      serverLoad,
      canKeepLocal: false,
      reason: EDIT_SYNC_CONFLICT_EVENT_STRUCTURE_CHANGED,
    };
  }

  if (!candidateAllowed) {
    return {
      serverLoad,
      canKeepLocal: false,
      reason: EDIT_SYNC_CONFLICT_LOCAL_SCHEDULE_INVALID,
    };
  }

  if (hasIntersection(localChanges, serverChanges)) {
    return {
      serverLoad,
      canKeepLocal: true,
      reason: EDIT_SYNC_CONFLICT_SAME_TIMELINE_FIELD_CHANGED,
    };
  }

  return null;
};

const getEventId = (event) => G.getGuidFromObject(event) || (event && event.id);

const hydrateLoadEvents = (load, events) => {
  if (!load) return load;

  const validEvents = events.filter(Boolean);
  const eventById = new Map(validEvents.map((event) => [getEventId(event), event]));

  const eventByIndex = new Map(validEvents.map((event) => [
    Number(event[GC.FIELD_TEL_EVENT_INDEX]),
    event,
  ]));

  return {
    ...load,
    events: (load.events || []).map((timelineEvent) => {
      const eventIndex = Number((timelineEvent.source || {})[GC.FIELD_TEL_EVENT_INDEX]);

      const event = eventById.get(timelineEvent.id)
        || (Number.isFinite(eventIndex) ? eventByIndex.get(eventIndex) : null);

      if (!event) return timelineEvent;

      return {
        ...timelineEvent,
        location: event.location || timelineEvent.location,
        source: { ...(timelineEvent.source || {}), ...event },
      };
    }),
    source: { ...(load.source || {}), events: validEvents },
  };
};

const hydrateSnapshotEvents = (snapshot, events) => {
  const hydrated = hydrateLoadEvents(snapshot, events);
  const changes = getRecordedDraftEventChanges(snapshot);

  return changes.size ? applyEditableChanges(hydrated, snapshot, changes) : hydrated;
};

const hydrateCommandEvents = (command, loadId, events) => (
  command.loadId === loadId
    ? {
      ...command,
      after: hydrateSnapshotEvents(command.after, events),
      before: hydrateSnapshotEvents(command.before, events),
    }
    : command
);

export const reconcileEditSessionLoad = (session, serverLoad, timelineRange) => {
  const loadId = serverLoad.id;
  const draftLoad = session.draftLoads.find(({ id }) => id === loadId);
  const baselineLoad = session.baselineLoads.find(({ id }) => id === loadId);

  if (!draftLoad) return session;

  const baselineExists = Boolean(baselineLoad);
  const serverBaseline = mergeServerLoad(baselineLoad || draftLoad, serverLoad);
  const rateOnly = isRouteBuilderLoad(draftLoad);
  const localChanges = getRebaseChanges(baselineLoad || serverBaseline, draftLoad, rateOnly);

  const serverChanges = baselineExists
    ? getEditableChanges(baselineLoad, serverBaseline)
    : new Set();

  const rebasedDraft = applyEditableChanges(serverBaseline, draftLoad, localChanges);

  const conflict = getConflict({
    localChanges,
    serverChanges,
    timelineRange,
    candidate: rebasedDraft,
    serverLoad: serverBaseline,
  });

  const baselineLoads = baselineExists
    ? replaceLoad(session.baselineLoads, loadId, serverBaseline)
    : session.baselineLoads;

  const draftLoads = replaceLoad(session.draftLoads, loadId, rebasedDraft);

  const syncConflicts = conflict
    ? { ...session.syncConflicts, [loadId]: conflict }
    : withoutKey(session.syncConflicts, loadId);

  return {
    ...session,
    draftLoads,
    baselineLoads,
    syncConflicts,
    serverRevisionByLoadId: {
      ...session.serverRevisionByLoadId,
      [loadId]: (session.serverRevisionByLoadId[loadId] || 0) + 1,
    },
    undoStack: rebaseUndoStack(
      session.undoStack,
      loadId,
      serverBaseline,
      rebasedDraft,
      baselineExists,
      rateOnly,
    ),
    dirty: !areTimelineLoadsEqual(baselineLoads, draftLoads),
    redoStack: rebaseRedoStack(session.redoStack, loadId, serverBaseline, rebasedDraft, rateOnly),
  };
};

export const hydrateEditSessionEvents = (session, loadId, events, timelineRange) => {
  const baselineLoad = session.baselineLoads.find(({ id }) => id === loadId);
  const draftLoad = session.draftLoads.find(({ id }) => id === loadId);

  if (!draftLoad) return session;

  const localChanges = baselineLoad
    ? getEditableChanges(baselineLoad, draftLoad)
    : getRecordedDraftEventChanges(draftLoad);

  const hydratedBaseline = hydrateLoadEvents(baselineLoad, events);
  const serverHydratedDraft = hydrateLoadEvents(draftLoad, events);

  const hydratedDraft = localChanges.size
    ? applyEditableChanges(serverHydratedDraft, draftLoad, localChanges)
    : serverHydratedDraft;

  const baselineLoads = baselineLoad
    ? replaceLoad(session.baselineLoads, loadId, hydratedBaseline)
    : session.baselineLoads;

  const draftLoads = replaceLoad(session.draftLoads, loadId, hydratedDraft);

  const conflict = baselineLoad && localChanges.size && !isTimelineEditAllowed(hydratedBaseline, {
    loadId,
    after: hydratedDraft,
    before: hydratedBaseline,
    kind: EDIT_COMMAND_HYDRATE_SERVER_EVENTS,
  }, timelineRange)
    ? {
      canKeepLocal: false,
      serverLoad: hydratedBaseline,
      reason: EDIT_SYNC_CONFLICT_LOCAL_SCHEDULE_INVALID,
    }
    : session.syncConflicts[loadId];

  const syncConflicts = conflict
    ? { ...session.syncConflicts, [loadId]: conflict }
    : withoutKey(session.syncConflicts, loadId);

  return {
    ...session,
    draftLoads,
    baselineLoads,
    syncConflicts,
    redoStack: session.redoStack.map((command) => hydrateCommandEvents(command, loadId, events)),
    undoStack: session.undoStack.map((command) => hydrateCommandEvents(command, loadId, events)),
  };
};

const retainInsertCommand = (commands, loadId, serverLoad, baselineExists) => {
  let insertRetained = false;

  return commands.reduce((result, command) => {
    if (command.loadId !== loadId) return [...result, command];

    if (baselineExists || insertRetained || command.kind !== EDIT_COMMAND_INSERT_LOAD) return result;

    insertRetained = true;

    return [...result, { ...command, before: null, after: cloneTimelineLoad(serverLoad) }];
  }, []);
};

export const resolveEditSessionConflict = (session, loadId, resolution) => {
  const conflict = session.syncConflicts[loadId];

  if (!conflict) return session;

  if (resolution === EDIT_SYNC_RESOLUTION_KEEP_LOCAL && conflict.canKeepLocal) {
    return {
      ...session,
      syncConflicts: withoutKey(session.syncConflicts, loadId),
    };
  }

  if (resolution !== EDIT_SYNC_RESOLUTION_USE_SERVER) return session;

  const baselineExists = session.baselineLoads.some(({ id }) => id === loadId);
  const draftLoads = replaceLoad(session.draftLoads, loadId, conflict.serverLoad);

  return {
    ...session,
    draftLoads,
    syncConflicts: withoutKey(session.syncConflicts, loadId),
    dirty: !areTimelineLoadsEqual(session.baselineLoads, draftLoads),
    redoStack: session.redoStack.filter((command) => command.loadId !== loadId),
    undoStack: retainInsertCommand(session.undoStack, loadId, conflict.serverLoad, baselineExists),
  };
};
